import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, User, Building, DollarSign, Search, CheckSquare, Square, ClipboardList, Calculator } from 'lucide-react';
import ApiService from '../services/ApiService';

const EnhancedWorkOrderModal = ({ title, workOrder, onClose, onSubmit }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', dueDate: '', assignedTo: '' });

  useEffect(() => {
    if (workOrder?.id) {
      setTasksLoading(true);
      ApiService.apiCall(`/workordertask/workorder/${workOrder.id}`)
        .then(data => setFollowUpTasks(data || []))
        .catch(() => {})
        .finally(() => setTasksLoading(false));
    }
  }, [workOrder?.id]);

  const addFollowUpTask = async () => {
    if (!newTask.title.trim()) return;
    if (workOrder?.id) {
      try {
        const created = await ApiService.apiCall('/workordertask', {
          method: 'POST',
          body: JSON.stringify({ workOrderId: workOrder.id, ...newTask, dueDate: newTask.dueDate || null })
        });
        setFollowUpTasks(prev => [...prev, created]);
      } catch (err) { alert('Failed to save task.'); return; }
    } else {
      setFollowUpTasks(prev => [...prev, { _localId: Date.now(), ...newTask, completed: false }]);
    }
    setNewTask({ title: '', description: '', dueDate: '', assignedTo: '' });
  };

  const toggleTask = async (task) => {
    if (workOrder?.id && task.id) {
      try {
        const updated = await ApiService.apiCall(`/workordertask/${task.id}`, {
          method: 'PUT',
          body: JSON.stringify({ title: task.title, description: task.description, dueDate: task.dueDate, assignedTo: task.assignedTo, completed: !task.completed })
        });
        setFollowUpTasks(prev => prev.map(t => t.id === task.id ? updated : t));
      } catch (err) { console.error(err); }
    } else {
      const key = task.id || task._localId;
      setFollowUpTasks(prev => prev.map(t => (t.id || t._localId) === key ? { ...t, completed: !t.completed } : t));
    }
  };

  const removeTask = async (task) => {
    if (workOrder?.id && task.id) {
      try { await ApiService.apiCall(`/workordertask/${task.id}`, { method: 'DELETE' }); }
      catch (err) { console.error(err); }
    }
    const key = task.id || task._localId;
    setFollowUpTasks(prev => prev.filter(t => (t.id || t._localId) !== key));
  };

  // ── Calculator state ──────────────────────────────────────────────────────
  const [calcItems, setCalcItems] = useState([]);
  const [calcProducts, setCalcProducts] = useState([]);
  const [calcApplied, setCalcApplied] = useState(false);

  useEffect(() => {
    ApiService.apiCall('/product').then(data => setCalcProducts(data || [])).catch(() => {});
  }, []);

  const addCalcProduct = (product) => {
    setCalcItems(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i => i.productId === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { id: Date.now(), productId: product.id, name: product.name, unitPrice: product.unitPrice, qty: 1, type: 'product' }];
    });
  };

  const addCalcMisc = () => {
    setCalcItems(prev => [...prev, { id: Date.now(), productId: null, name: '', unitPrice: 0, qty: 1, type: 'misc' }]);
  };

  const updateCalcItem = (id, field, value) => {
    setCalcItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const removeCalcItem = (id) => {
    setCalcItems(prev => prev.filter(i => i.id !== id));
  };

  const [calcTargetIndex, setCalcTargetIndex] = useState(0);
  const [invoicePercent, setInvoicePercent] = useState(75);
  const [editingPercent, setEditingPercent] = useState(false);

  const applyPercentToSubcontractor = () => {
    handleLineItemChange(calcTargetIndex, 'cost', percentAmount.toFixed(2));
    setCalcApplied(true);
    setEditingPercent(false);
    setTimeout(() => setCalcApplied(false), 3000);
  };

  const [showCalc, setShowCalc] = useState(false);

  const applyCalcToSubcontractor = () => {
    handleLineItemChange(calcTargetIndex, 'cost', calcTotal.toFixed(2));
    setCalcApplied(true);
    setTimeout(() => setCalcApplied(false), 3000);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState({
    workOrderNumber: workOrder?.workOrderNumber || '',
    clientName: workOrder?.clientName || '',
    xeroContactId: workOrder?.xeroContactId || '',
    invoiceAmount: workOrder?.invoiceAmount || 0,
    originalInvoiceAmount: workOrder?.originalInvoiceAmount || 0,
    clientDiscountPercentage: workOrder?.clientDiscountPercentage || 0,
    status: workOrder?.status || 'Pending',
    location: workOrder?.location || '',
    description: workOrder?.description || '',
    serviceType: workOrder?.serviceType || '',
    priority: workOrder?.priority || 'Normal',
    dueDate: workOrder?.dueDate ? workOrder.dueDate.split('T')[0] : '',
    department: workOrder?.department || '',
    team: workOrder?.team || '',
    notes: workOrder?.notes || '',
    tags: workOrder?.tags || ''
  });

  const [lineItems, setLineItems] = useState(
    workOrder?.lineItems || [
      {
        id: Date.now(),
        subcontractorName: '',
        subcontractorContactId: '',
        cost: 0,
        description: '',
        serviceType: '',
        notes: ''
      }
    ]
  );

  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [contactSearchTerm, setContactSearchTerm] = useState('');

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setIsLoadingContacts(true);
      // Use enhanced contacts endpoint to get contacts with their enhancements
      const contactsData = await ApiService.apiCall('/contactenhancement/enhanced-contacts');
      console.log('📞 Loaded contacts for work order modal:', contactsData);
      console.log('📊 Contact breakdown:', {
        total: contactsData?.length || 0,
        customers: contactsData?.filter(c => c.isCustomer || c.IsCustomer).length || 0,
        suppliers: contactsData?.filter(c => c.isSupplier || c.IsSupplier).length || 0
      });
      setContacts(contactsData || []);
    } catch (error) {
      console.error('Error loading enhanced contacts:', error);
      // Use mock contacts if API fails
      setContacts([
        { 
          contactID: 'client-1', 
          name: 'ABC Company', 
          isCustomer: true,
          enhancement: { defaultTeam: 'rich', defaultDiscountPercentage: 25, defaultDepartment: 'GAM' }
        },
        { 
          contactID: 'client-2', 
          name: 'XYZ Corp', 
          isCustomer: true,
          enhancement: { defaultTeam: 'garrett', defaultDiscountPercentage: 20, defaultDepartment: 'MCS' }
        },
        { 
          contactID: 'contractor-1', 
          name: 'John Smith Contractor', 
          isCustomer: false,
          enhancement: { defaultTeam: 'jason', defaultDiscountPercentage: 15 }
        }
      ]);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Auto-assign team from first subcontractor's contact enhancement if team is not already set
    let teamToAssign = formData.team;
    if (!teamToAssign && lineItems.length > 0 && lineItems[0].subcontractorContactId) {
      // Find the contact and get their default team from enhancement
      const firstSubcontractor = contacts.find(c => c.contactID === lineItems[0].subcontractorContactId);
      if (firstSubcontractor?.enhancement?.defaultTeam) {
        teamToAssign = firstSubcontractor.enhancement.defaultTeam;
        console.log('🎯 Auto-assigning team from contact enhancement:', teamToAssign);
      } else if (firstSubcontractor?.Enhancement?.DefaultTeam) {
        // Handle different casing
        teamToAssign = firstSubcontractor.Enhancement.DefaultTeam;
        console.log('🎯 Auto-assigning team from contact Enhancement:', teamToAssign);
      }
    }
    
    const submitData = {
      ...formData,
      team: teamToAssign, // Set the team from first subcontractor's contact enhancement
      invoiceAmount: parseFloat(formData.invoiceAmount) || 0,
      originalInvoiceAmount: parseFloat(formData.originalInvoiceAmount) || parseFloat(formData.invoiceAmount) || 0,
      dueDate: formData.dueDate || null,
      lineItems: lineItems.map(item => ({
        subcontractorName: item.subcontractorName,
        subcontractorContactId: item.subcontractorContactId,
        cost: parseFloat(item.cost) || 0,
        description: item.description,
        serviceType: item.serviceType,
        notes: item.notes
      }))
    };

    console.log('📤 Submitting work order with team:', teamToAssign);
    onSubmit({ ...submitData, followUpTasks });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const calcTotal = calcItems.reduce((sum, i) => sum + (parseFloat(i.qty) || 0) * (parseFloat(i.unitPrice) || 0), 0);
  const percentAmount = (parseFloat(formData.invoiceAmount) || 0) * ((parseFloat(invoicePercent) || 0) / 100);

  const addLineItem = () => {
    setLineItems(prev => [
      ...prev,
      {
        id: Date.now(),
        subcontractorName: '',
        subcontractorContactId: '',
        cost: 0,
        description: '',
        serviceType: '',
        notes: ''
      }
    ]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const getTotalCost = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
  };

  const getProfit = () => {
    return (parseFloat(formData.invoiceAmount) || 0) - getTotalCost();
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(contactSearchTerm.toLowerCase())
  );

  const clientContacts = filteredContacts.filter(c => c.isCustomer);
  const supplierContacts = filteredContacts.filter(c => c.isSupplier);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Work Order Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${activeTab === 'tasks' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <ClipboardList className="w-4 h-4" />
              Follow-up Tasks
              {followUpTasks.length > 0 && (
                <span className="ml-1 bg-blue-100 text-blue-700 text-xs rounded-full px-1.5 py-0.5">
                  {followUpTasks.filter(t => !t.completed).length}/{followUpTasks.length}
                </span>
              )}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Details Tab */}
            <div className={activeTab !== 'details' ? 'hidden' : ''}>

            {/* Basic Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Order Number *
                  </label>
                  <input
                    type="text"
                    name="workOrderNumber"
                    value={formData.workOrderNumber}
                    onChange={handleChange}
                    required
                    disabled={!!workOrder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work Order Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Assigned">Assigned</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5" />
                Client Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client (from Xero Contacts) *
                  </label>
                  {isLoadingContacts ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                      Loading contacts...
                    </div>
                  ) : (
                    <select
                      name="xeroContactId"
                      value={formData.xeroContactId}
                      onChange={(e) => {
                        const selectedContact = contacts.find(c => c.contactID === e.target.value);
                        
                        // Calculate discounted invoice amount if client has default discount
                        let finalInvoiceAmount = parseFloat(formData.invoiceAmount) || 0;
                        if (selectedContact?.enhancement?.defaultDiscountPercentage > 0) {
                          const discountPercent = selectedContact.enhancement.defaultDiscountPercentage;
                          const discountAmount = (finalInvoiceAmount * discountPercent) / 100;
                          finalInvoiceAmount = finalInvoiceAmount - discountAmount;
                        }
                        
                        setFormData(prev => ({
                          ...prev,
                          xeroContactId: e.target.value,
                          clientName: selectedContact ? selectedContact.name : '',
                          // Auto-populate from contact enhancement
                          team: selectedContact?.enhancement?.defaultTeam || prev.team,
                          department: selectedContact?.enhancement?.defaultDepartment || prev.department,
                          // Apply discount to invoice amount
                          invoiceAmount: finalInvoiceAmount,
                          originalInvoiceAmount: parseFloat(prev.invoiceAmount) || 0,
                          clientDiscountPercentage: selectedContact?.enhancement?.defaultDiscountPercentage || 0
                        }));
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select a client...</option>
                      <optgroup label="Customers">
                        {clientContacts.map(contact => (
                          <option key={contact.contactID} value={contact.contactID}>
                            {contact.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Amount * (After Discount)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="invoiceAmount"
                    value={formData.invoiceAmount}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  {formData.clientDiscountPercentage > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      Original: ${(formData.originalInvoiceAmount || 0).toFixed(2)} | 
                      Client Discount: {formData.clientDiscountPercentage}% 
                      (-${((formData.originalInvoiceAmount || 0) * (formData.clientDiscountPercentage || 0) / 100).toFixed(2)})
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Amount to charge the client
                  </p>
                </div>
              </div>
            </div>

            {/* Subcontractors Section */}
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-purple-800 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Subcontractors & Costs
                </h3>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg flex items-center gap-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Subcontractor
                </button>
              </div>

              {lineItems.map((item, index) => (
                <div key={item.id} className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-gray-800">Subcontractor #{index + 1}</h4>
                    {lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subcontractor (from Xero Contacts) *
                      </label>
                      <select
                        value={item.subcontractorContactId}
                        onChange={(e) => {
                          const selectedContact = contacts.find(c => c.contactID === e.target.value);
                          handleLineItemChange(index, 'subcontractorContactId', e.target.value);
                          handleLineItemChange(index, 'subcontractorName', selectedContact ? selectedContact.name : '');
                          
                          // Auto-populate team from first subcontractor's default team if available and no team is set
                          if (index === 0 && !formData.team && selectedContact?.enhancement?.defaultTeam) {
                            setFormData(prev => ({
                              ...prev,
                              team: selectedContact.enhancement.defaultTeam
                            }));
                            console.log('🎯 Auto-assigned team from contact:', selectedContact.enhancement.defaultTeam);
                          } else if (index === 0 && !formData.team && selectedContact?.Enhancement?.DefaultTeam) {
                            // Handle different casing
                            setFormData(prev => ({
                              ...prev,
                              team: selectedContact.Enhancement.DefaultTeam
                            }));
                            console.log('🎯 Auto-assigned team from contact (alt casing):', selectedContact.Enhancement.DefaultTeam);
                          }
                        }}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Select a subcontractor...</option>
                        <optgroup label="Suppliers/Contractors">
                          {supplierContacts.map(contact => (
                            <option key={contact.contactID} value={contact.contactID}>
                              {contact.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cost *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.cost}
                        onChange={(e) => handleLineItemChange(index, 'cost', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Amount to pay this subcontractor
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Type
                      </label>
                      <input
                        type="text"
                        value={item.serviceType}
                        onChange={(e) => handleLineItemChange(index, 'serviceType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={item.notes}
                      onChange={(e) => handleLineItemChange(index, 'notes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              ))}

              {/* Cost Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Invoice Amount</p>
                    <p className="text-lg font-semibold text-green-600">
                      ${(parseFloat(formData.invoiceAmount) || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Cost</p>
                    <p className="text-lg font-semibold text-purple-600">
                      ${getTotalCost().toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Profit</p>
                    <p className={`text-lg font-semibold ${getProfit() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${getProfit().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Price Calculator */}
              <div className="mt-4 border border-purple-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCalc(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-800 text-sm font-medium transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Price Calculator
                    {calcApplied && <span className="bg-green-100 text-green-700 text-xs rounded-full px-2 py-0.5">Applied!</span>}
                  </span>
                  <span>{showCalc ? '▲' : '▼'}</span>
                </button>

                {showCalc && (
                  <div className="p-4 space-y-3 bg-white">
                    {/* Product picker */}
                    {calcProducts.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Quick-add a product:</p>
                        <div className="flex flex-wrap gap-2">
                          {calcProducts.map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => addCalcProduct(p)}
                              className="flex items-center gap-1 bg-gray-50 border border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-sm px-3 py-1.5 rounded-lg transition-colors"
                            >
                              <span className="font-medium text-gray-800">{p.name}</span>
                              <span className="text-gray-400 text-xs">${(p.unitPrice || 0).toFixed(2)}{p.unit ? `/${p.unit}` : ''}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Calc line items */}
                    <div className="space-y-2">
                      {calcItems.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-3">No items yet. Pick a product or add a misc item.</p>
                      )}
                      {calcItems.map(item => (
                        <div key={item.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          <div className="flex-1">
                            {item.type === 'misc' ? (
                              <input
                                type="text"
                                placeholder="Description"
                                value={item.name}
                                onChange={e => updateCalcItem(item.id, 'name', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              <span className="text-sm font-medium text-gray-800">{item.name}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-gray-500">Qty</label>
                            <input type="number" min="1" step="any" value={item.qty}
                              onChange={e => updateCalcItem(item.id, 'qty', e.target.value)}
                              className="w-14 px-2 py-1 border border-gray-300 rounded text-sm text-center" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-gray-500">@</label>
                            <input type="number" step="0.01" min="0" value={item.unitPrice}
                              onChange={e => updateCalcItem(item.id, 'unitPrice', e.target.value)}
                              className="w-22 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
                          </div>
                          <span className="text-sm font-semibold text-gray-700 w-18 text-right">
                            ${((parseFloat(item.qty) || 0) * (parseFloat(item.unitPrice) || 0)).toFixed(2)}
                          </span>
                          <button type="button" onClick={() => removeCalcItem(item.id)} className="text-red-400 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <button type="button" onClick={addCalcMisc}
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800">
                      <Plus className="w-3 h-3" /> Add misc item
                    </button>

                    {/* % of Invoice quick-apply */}
                    <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-4 py-3">
                      <span className="text-xs text-gray-500 whitespace-nowrap">% of Invoice:</span>
                      {editingPercent ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={invoicePercent}
                          onChange={e => setInvoicePercent(e.target.value)}
                          onBlur={() => setEditingPercent(false)}
                          autoFocus
                          className="w-16 px-2 py-1 border border-purple-400 rounded text-sm text-center focus:ring-2 focus:ring-purple-500"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingPercent(true)}
                          className="w-16 px-2 py-1 bg-white border border-gray-300 rounded text-sm text-center hover:border-purple-400 font-medium"
                          title="Click to edit percentage"
                        >
                          {invoicePercent}%
                        </button>
                      )}
                      <span className="text-sm font-semibold text-gray-700">=&nbsp;${percentAmount.toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={applyPercentToSubcontractor}
                        className="ml-auto bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap"
                      >
                        Apply to Cost
                      </button>
                    </div>

                    {/* Total + apply to subcontractor */}
                    <div className="flex items-center justify-between bg-purple-50 rounded-lg px-4 py-3 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-xl font-bold text-gray-800">${calcTotal.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <select
                          value={calcTargetIndex}
                          onChange={e => setCalcTargetIndex(Number(e.target.value))}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        >
                          {lineItems.map((item, idx) => (
                            <option key={idx} value={idx}>
                              Sub #{idx + 1}{item.subcontractorName ? ` — ${item.subcontractorName}` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={applyCalcToSubcontractor}
                          disabled={calcItems.length === 0}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 whitespace-nowrap"
                        >
                          Apply to Cost
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    placeholder="e.g., GAM, MCS"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                  </label>
                  <input
                    type="text"
                    name="team"
                    value={formData.team}
                    onChange={handleChange}
                    placeholder="e.g., rich, garrett, jason"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            </div>{/* end Details Tab */}

            {/* Follow-up Tasks Tab */}
            <div className={activeTab !== 'tasks' ? 'hidden' : 'space-y-4'}>

              {/* Always-visible add form */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Task title *"
                    value={newTask.title}
                    onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFollowUpTask(); } }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addFollowUpTask}
                    disabled={!newTask.title.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newTask.description}
                    onChange={e => setNewTask(p => ({ ...p, description: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Assigned to (optional)"
                    value={newTask.assignedTo}
                    onChange={e => setNewTask(p => ({ ...p, assignedTo: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={e => setNewTask(p => ({ ...p, dueDate: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              {followUpTasks.length > 0 && (
                <p className="text-xs text-gray-400">
                  {followUpTasks.filter(t => t.completed).length} of {followUpTasks.length} completed
                </p>
              )}

              {/* Task List */}
              {tasksLoading ? (
                <p className="text-sm text-gray-400 py-4 text-center">Loading tasks...</p>
              ) : followUpTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No follow-up tasks yet.</p>
                  <p className="text-xs mt-1">Add tasks to track post-completion work.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {followUpTasks.map(task => (
                    <div
                      key={task.id || task._localId}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${task.completed ? 'bg-gray-50 border-gray-200 opacity-70' : 'bg-white border-gray-200'}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleTask(task)}
                        className={`mt-0.5 flex-shrink-0 ${task.completed ? 'text-green-500' : 'text-gray-400 hover:text-blue-500'}`}
                      >
                        {task.completed ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${task.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                        )}
                        <div className="flex gap-3 mt-1">
                          {task.dueDate && (
                            <span className="text-xs text-gray-400">Due: {task.dueDate?.split('T')[0] || task.dueDate}</span>
                          )}
                          {task.assignedTo && (
                            <span className="text-xs text-gray-400">Assigned: {task.assignedTo}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTask(task)}
                        className="flex-shrink-0 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>{/* end Follow-up Tasks Tab */}


            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {workOrder ? 'Update' : 'Create'} Work Order
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnhancedWorkOrderModal;
